import { defineStore } from "pinia";
import { registerEvents } from "@/composables/components";
import * as DATA$farm from '@/apis/data/farm';

let t;

const dec2format = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const dec2 = value => {
    if (Number.isNaN(value) || value === undefined) return "-";
    // return value;
    return dec2format.format(value);
  };

export const register = () => {    
    const compName = "MiniCorporateEvents";

    DATA$farm.register(compName);    
    registerEvents(compName);
};

const getRecent = (items, options) => {
    const now = new Date();
    const sixtyDays = 1000 * 60 * 60 * 24 * options.validRange;

    return items.filter(item => {
        const eventDate = new Date(item.date);
        const diff = Math.abs(eventDate - now);

        return diff < sixtyDays;
    });
};



const parseEarnings = (items, options) => {
    const parsing = items.slice(0, options.tooltip.max);

    parsing.forEach(item => {
        item.text = `${t('corpevents.earningseventfor')} ${t(`corpevents.${item.quarter}`)} ${item.fiscalyear}. `

        if (item.eps) {
            item.text += `${t('corpevents.eps')} ${t('labels.of')} ${dec2(item.eps)}. `;
        }

        if (item.url) {
            item.eventInfoURL = item.url;
        }
    });        

    return {
        items: parsing,
        showMore: items.length > options.tooltip.max
    }
};

const parseDividends = (items, options) => {
    const parsing = items.slice(0, options.tooltip.max);

    parsing.forEach(item => {
        item.text = `${t('corpevents.exdivdatefor')} ${t('corpevents.divincome')} ${t('labels.of')} ${item.amount}.`
    });        

    return {
        items: parsing,
        showMore: items.length > options.tooltip.max
    }
};

const parseSurprises = (items, options) => {    
    const parsing = items.slice(0, options.tooltip.max);
    const translationHash = {
        'Positive Surprise': 'corpevents.positivesurprise',
        'Negative Surprise': 'corpevents.negativesurprise',
        'No Surprise': 'corpevents.nosurprise',
        'N/A': 'corpevents.nosurprise'
    };

    parsing.forEach(item => {
        const text = [];
        if (item.type === 'No Surprise' || item.type === 'N/A') {
            text.push(t(translationHash[item.type]));
        } else {
            text.push(`${t(translationHash[item.type])} ${t('labels.of')} ${item.surprise.toFixed(2)}%.`);
        }

        if (item.expeps) {
            text.push(`${t('corpevents.expectedeps')} ${dec2(item.expeps)}; `);
        }

        if (item.actualeps) {
            text.push(`${t('corpevents.actualeps')} ${dec2(item.actualeps)}`);
        }

        item.text = text.join('');
    });

    return {
        items: parsing,
        showMore: items.length > options.tooltip.max
    }
};

const parseRatings = (items, options) => {
    const parsing = items.slice(0, options.tooltip.max);

    parsing.forEach(item => {
        if (item.prevrating) {
            item.text = `${item.broker} ${t('corpevents.announces')} ${t(`corpevents.${item.type.toLowerCase()}`)} from ${t(`corpevents.${item.prevrating.replace(' ', '').toLowerCase()}`)} to ${t(`corpevents.${item.rating.replace(' ', '').toLowerCase()}`)}. `;
        } else {
            item.text = `${item.broker} ${t('corpevents.announces')} ${t(`corpevents.${item.type.toLowerCase()}`)} to ${t(`corpevents.${item.rating.replace(' ', '').toLowerCase()}`)}. `;
        }

        if (item.lowtarget !== '0.0') {
            item.text += `${t('corpevents.lowtarget')} ${item.lowtarget}`;
        }

    });        

    return {
        items: parsing,
        showMore: items.length > options.tooltip.max
    }
};

const parseSplits = (items, options) => {
    const parsing = items.slice(0, options.tooltip.max);

    parsing.forEach(item => {
        item.text = `${t('corpevents.stocksplits')} ${item.tofactor}:${item.forfactor} (${t('corpevents.splitratio')} ${item.splitratio.toFixed(2)})`;
    });        

    return {
        items: parsing,
        showMore: items.length > options.tooltip.max
    }
};

export const useMiniCorporateEventsStore = defineStore("minicorporateevents", {
    state: () => {
        return { 
            options: {},
            nodata: false,
            data: {}
        };
    },
    
    getters: {
        earnings() {
            return this.data.earnings;
        },

        splits() {
            return this.data.splits;
        },

        surprises() {
            return this.data.surprises;
        },

        dividends() {
            return this.data.dividends;
        },

        ratings() {
            return this.data.ratings;
        }
    },

    actions: {
        init(options) {
            this.options = options;

            t = options.t;
        },
        
        async fetch() {
            try {
                // Run API calls in parallel using Promise.all
                const [
                    dividendsResponse,
                    earningsResponse,
                    ratingsResponse,
                    surprisesResponse,
                    splitsResponse
                ] = await Promise.all([
                    DATA$farm.getDividendsBySymbol(this.options.symbol),
                    DATA$farm.getEarningsEventsBySymbol(this.options.symbol),
                    DATA$farm.getBrokerRatingsBySymbol(this.options.symbol),
                    DATA$farm.getEarningsSurprisesBySymbol(this.options.symbol),
                    DATA$farm.getSplitsBySymbol(this.options.symbol)
                ]);
                
                const { results: dividends } = dividendsResponse;
                const { results: earnings } = earningsResponse;
                const { results: ratings } = ratingsResponse;
                const { results: surprises } = surprisesResponse;
                const { results: splits } = splitsResponse;
                
                // Process data in parallel where possible
                const dividendsData = parseDividends(getRecent(dividends.dividends[0]?.dividend || [], this.options), this.options);
                const earningsData = earnings.count > 0 ? parseEarnings(getRecent(earnings.earningsevents.event, this.options), this.options) : {items: []};
                const surprisesData = parseSurprises(getRecent(surprises.earningssurprises[0]?.earningssurprise || [], this.options), this.options);
                const ratingsData = parseRatings(getRecent(ratings.brokerratings[0]?.brokerrating || [], this.options), this.options);
                const splitsData = parseSplits(getRecent(splits.splits[0]?.split || [], this.options), this.options);
                
                // Update state with processed data
                this.data.dividends = dividendsData;
                this.data.earnings = earningsData;
                this.data.surprises = surprisesData;
                this.data.ratings = ratingsData;
                this.data.splits = splitsData;

                const totalEventCount = [
                    this.data.dividends.items, 
                    this.data.earnings.items, 
                    this.data.surprises.items, 
                    this.data.ratings.items, 
                    this.data.splits.items
                ].reduce((n, i) => n + i.length, 0);

                this.nodata = totalEventCount === 0;
            } catch (error) {
                console.error('Error fetching corporate events data:', error);
                this.nodata = true;
            }
        },
    },
});
